import {
  type CSSProperties,
  type FormEvent,
  type ReactNode,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  BusFront,
  CalendarDays,
  Check,
  ChevronDown,
  ExternalLink,
  MapPinned,
  Menu,
  Send,
  X,
} from "lucide-react";
import { LiveGalleryPage } from "./LiveGalleryPage";
import {
  checklistItems,
  faqItems,
  galleryItems,
  guestFeatures,
  infoCards,
  navItems,
  returnTransportOptions,
  timeline,
} from "./content";
import { Seal, Sprig } from "./ornaments";
import { shouldRenderLiveGallery } from "./routes";
import { pl } from "./text";

type QuestionState = {
  name: string;
  email: string;
  message: string;
  website: string;
};

const initialQuestionState: QuestionState = {
  name: "",
  email: "",
  message: "",
  website: "",
};

type RsvpState = {
  names: string;
  contactPhone: string;
  attendance: "yes" | "no" | "";
  foodAllergies: string;
  hotel: "yes" | "no" | "";
  outboundBus: "yes" | "no" | "";
  returnBus: "00:00" | "02:30" | "04:30" | "none" | "";
  website: string;
};

const initialRsvpState: RsvpState = {
  names: "",
  contactPhone: "",
  attendance: "",
  foodAllergies: "",
  hotel: "",
  outboundBus: "",
  returnBus: "",
  website: "",
};

type SubmitState = "idle" | "submitting" | "success" | "error";

function SectionHeading({
  eyebrow,
  title,
  lede,
}: {
  eyebrow: string;
  title: string;
  lede?: string;
}) {
  return (
    <div className="section-heading" data-reveal>
      <Sprig />
      <p className="eyebrow">{eyebrow}</p>
      <h2>{pl(title)}</h2>
      {lede ? <p className="section-lede">{pl(lede)}</p> : null}
    </div>
  );
}

function useScrolled(threshold = 32) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > threshold);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [threshold]);
  return scrolled;
}

function useReveal() {
  useEffect(() => {
    const elements = Array.from(
      document.querySelectorAll<HTMLElement>("[data-reveal]"),
    );
    const reduceMotion = window.matchMedia(
      "(prefers-reduced-motion: reduce)",
    ).matches;

    if (reduceMotion || !("IntersectionObserver" in window)) {
      elements.forEach((element) => element.classList.add("is-visible"));
      return;
    }

    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            entry.target.classList.add("is-visible");
            observer.unobserve(entry.target);
          }
        }
      },
      { threshold: 0.12, rootMargin: "0px 0px -8% 0px" },
    );

    elements.forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, []);
}

function NavOverlay({ onClose }: { onClose: () => void }) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const previouslyFocused = document.activeElement;
    closeRef.current?.focus();
    return () => {
      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus();
      }
    };
  }, []);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        onClose();
        return;
      }
      if (event.key !== "Tab" || !overlayRef.current) {
        return;
      }
      const focusables = overlayRef.current.querySelectorAll<HTMLElement>(
        "a[href], button:not([disabled])",
      );
      if (focusables.length === 0) {
        return;
      }
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      const active = document.activeElement;
      const inside = active instanceof HTMLElement && overlayRef.current.contains(active);
      if (event.shiftKey && (active === first || !inside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (active === last || !inside)) {
        event.preventDefault();
        first.focus();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  return (
    <div
      ref={overlayRef}
      className="nav-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Menu"
    >
      <button
        ref={closeRef}
        className="nav-overlay-close"
        type="button"
        onClick={onClose}
        aria-label="Zamknij menu"
      >
        <X aria-hidden="true" size={22} />
      </button>
      <nav className="nav-overlay-links">
        {navItems.map((item, index) => (
          <a
            key={item.href}
            href={item.href}
            style={{ animationDelay: `${90 + index * 65}ms` }}
            onClick={onClose}
          >
            <span className="nav-overlay-index">
              {String(index + 1).padStart(2, "0")}
            </span>
            {item.label}
          </a>
        ))}
      </nav>
      <div className="nav-overlay-foot">
        <Sprig />
        <p>Ewelina i Mateusz — do zobaczenia!</p>
      </div>
    </div>
  );
}

function FormField({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <label className="form-field">
      <span className="form-label">{label}</span>
      {children}
    </label>
  );
}

function ChoiceGroup({
  legend,
  name,
  options,
  value,
  onChange,
  wideLast = false,
}: {
  legend: string;
  name: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  value: string;
  onChange: (value: string) => void;
  wideLast?: boolean;
}) {
  return (
    <fieldset className="choice-group">
      <legend className="form-label">{legend}</legend>
      <div className={wideLast ? "choice-grid choice-grid-wide-last" : "choice-grid"}>
        {options.map((option) => (
          <label className="choice-option" key={option.value}>
            <input
              required
              type="radio"
              name={name}
              value={option.value}
              checked={value === option.value}
              onChange={() => onChange(option.value)}
            />
            <span className="choice-control" aria-hidden="true" />
            <span>{option.label}</span>
          </label>
        ))}
      </div>
    </fieldset>
  );
}

export function App() {
  return shouldRenderLiveGallery(window.location.pathname) ? (
    <LiveGalleryPage />
  ) : (
    <HomePage />
  );
}

function HomePage() {
  const [question, setQuestion] = useState<QuestionState>(initialQuestionState);
  const [submitState, setSubmitState] = useState<SubmitState>("idle");
  const [submitMessage, setSubmitMessage] = useState("");
  const [rsvp, setRsvp] = useState<RsvpState>(initialRsvpState);
  const [rsvpSubmitState, setRsvpSubmitState] = useState<SubmitState>("idle");
  const [rsvpSubmitMessage, setRsvpSubmitMessage] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);
  const scrolled = useScrolled();

  useReveal();

  useEffect(() => {
    document.documentElement.classList.toggle("menu-locked", menuOpen);
    return () => document.documentElement.classList.remove("menu-locked");
  }, [menuOpen]);

  function updateQuestion(field: keyof QuestionState, value: string) {
    setQuestion((current) => ({ ...current, [field]: value }));
    if (submitState !== "idle") {
      setSubmitState("idle");
      setSubmitMessage("");
    }
  }

  function updateRsvp<Field extends keyof RsvpState>(
    field: Field,
    value: RsvpState[Field],
  ) {
    setRsvp((current) => {
      if (field === "attendance" && value === "no") {
        return {
          ...current,
          attendance: "no",
          foodAllergies: "",
          hotel: "",
          outboundBus: "",
          returnBus: "",
        };
      }
      return { ...current, [field]: value };
    });
    if (rsvpSubmitState !== "idle") {
      setRsvpSubmitState("idle");
      setRsvpSubmitMessage("");
    }
  }

  async function handleRsvpSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setRsvpSubmitState("submitting");
    setRsvpSubmitMessage("");

    try {
      const response = await fetch("/api/rsvp", {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify(rsvp),
      });
      const result = (await response.json().catch(() => ({}))) as {
        error?: string;
      };

      if (!response.ok) {
        throw new Error(result.error ?? "Nie udało się wysłać potwierdzenia.");
      }

      setRsvp(initialRsvpState);
      setRsvpSubmitState("success");
      setRsvpSubmitMessage("Odpowiedź zapisana. Dziękujemy!");
    } catch (error) {
      setRsvpSubmitState("error");
      setRsvpSubmitMessage(
        error instanceof Error
          ? error.message
          : "Nie udało się wysłać potwierdzenia. Spróbuj ponownie za chwilę.",
      );
    }
  }

  async function handleQuestionSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    setSubmitState("submitting");
    setSubmitMessage("");

    try {
      const response = await fetch("/api/contact", {
        method: "POST",
        headers: {
          "content-type": "application/json",
        },
        body: JSON.stringify(question),
      });
      const result = (await response.json().catch(() => ({}))) as {
        error?: string;
      };

      if (!response.ok) {
        throw new Error(result.error ?? "Nie udało się wysłać wiadomości.");
      }

      setQuestion(initialQuestionState);
      setSubmitState("success");
      setSubmitMessage("Wiadomość wysłana. Odpowiemy najszybciej, jak się da.");
    } catch (error) {
      setSubmitState("error");
      setSubmitMessage(
        error instanceof Error
          ? error.message
          : "Nie udało się wysłać wiadomości. Spróbuj ponownie za chwilę.",
      );
    }
  }

  return (
    <div className="site-shell">
      <header className={scrolled ? "topbar is-scrolled" : "topbar"}>
        <div className="topbar-inner">
          <a className="brand" href="#start" aria-label="Ewelina i Mateusz — początek strony">
            <Seal className="brand-seal" />
            <span className="brand-name">
              Ewelina <em>i</em> Mateusz
            </span>
          </a>
          <nav className="nav-desktop" aria-label="Główna nawigacja">
            {navItems.map((item) => (
              <a key={item.href} href={item.href}>
                {item.label}
              </a>
            ))}
          </nav>
          <button
            className="nav-toggle"
            type="button"
            onClick={() => setMenuOpen(true)}
            aria-label="Otwórz menu"
            aria-haspopup="dialog"
            aria-expanded={menuOpen}
          >
            <Menu aria-hidden="true" size={20} />
          </button>
        </div>
      </header>

      {menuOpen ? <NavOverlay onClose={() => setMenuOpen(false)} /> : null}

      <main>
        <section className="hero" id="start">
          <div className="hero-image" aria-hidden="true" />
          <div className="hero-scrim" aria-hidden="true" />
          <div className="hero-frame" aria-hidden="true" />
          <div className="hero-content">
            <p className="hero-kicker" data-load="1">
              Ślub & wesele
            </p>
            <h1 className="hero-names">
              <span className="hero-name" data-load="2">
                Ewelina
              </span>
              <span className="hero-script" data-load="3">
                i
              </span>
              <span className="hero-name" data-load="4">
                Mateusz
              </span>
            </h1>
            <p className="hero-datebar" data-load="5">
              <span className="datebar-rule" aria-hidden="true" />
              <span>10 lipca 2027 · Kraków</span>
              <span className="datebar-rule" aria-hidden="true" />
            </p>
            <p className="hero-lede" data-load="6">
              {pl(
                "Pobieramy się! Zapraszamy Was na ceremonię i przyjęcie w La Palette Garden. Wszystkie szczegóły organizacyjne zbieramy tutaj.",
              )}
            </p>
            <div className="hero-actions" data-load="7">
              <a className="button paper" href="#potwierdzenie">
                Potwierdź obecność
              </a>
              <a className="button ghost" href="#miejsce">
                Zobacz miejsce
              </a>
            </div>
          </div>
          <a className="hero-scroll" href="#informacje" aria-label="Przewiń do informacji">
            <ChevronDown aria-hidden="true" size={20} />
          </a>
        </section>

        <aside className="ribbon" aria-label="Najważniejsze fakty">
          <p>
            <strong>Data</strong>
            <span>10 lipca 2027</span>
          </p>
          <p>
            <strong>Miejsce</strong>
            <span>La Palette Garden</span>
          </p>
          <p>
            <strong>Godzina</strong>
            <span>podamy wkrótce</span>
          </p>
        </aside>

        <section className="section" id="informacje">
          <SectionHeading
            eyebrow="Dla gości"
            title="Wszystko, co trzeba wiedzieć"
            lede="Mamy już datę i miejsce. Godzinę rozpoczęcia oraz pozostałe szczegóły będziemy uzupełniać tutaj."
          />
          <div className="info-grid">
            {infoCards.map((card, index) => (
              <article
                className="info-card"
                key={card.label}
                data-reveal
                style={{ "--reveal-delay": `${index * 80}ms` } as CSSProperties}
              >
                <p className="info-num" aria-hidden="true">
                  {String(index + 1).padStart(2, "0")}
                </p>
                <p className="card-label">{card.label}</p>
                <h3>{pl(card.title)}</h3>
                <p>{pl(card.text)}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section split-section" id="miejsce">
          <div className="split-copy" data-reveal>
            <Sprig className="sprig-left" />
            <p className="eyebrow">Miejsce</p>
            <h2>{pl("La Palette Garden, Kraków")}</h2>
            <p className="section-lede">
              {pl(
                "Ceremonia i przyjęcie odbędą się w jednym miejscu: przy ul. Gustawa Morcinka 40, 31-762 Kraków. Dokładną godzinę rozpoczęcia podamy później.",
              )}
            </p>
            <div className="feature-list">
              {guestFeatures.map((feature) => {
                const Icon = feature.icon;
                return (
                  <article className="feature-row" key={feature.title}>
                    <span className="feature-icon">
                      <Icon aria-hidden="true" size={19} strokeWidth={1.7} />
                    </span>
                    <div>
                      <h3>{feature.title}</h3>
                      <p>{pl(feature.text)}</p>
                    </div>
                  </article>
                );
              })}
            </div>
          </div>

          <section className="map-card" data-reveal aria-label="Miejsce uroczystości">
            <div className="map-canvas" aria-hidden="true">
              <svg viewBox="0 0 320 190" focusable="false">
                <path
                  className="map-route"
                  d="M18 158 C 70 150, 92 96, 148 92 S 250 66, 296 34"
                  fill="none"
                  strokeWidth="1.6"
                  strokeDasharray="1 8"
                  strokeLinecap="round"
                />
                <circle className="map-dot" cx="18" cy="158" r="4" />
                <circle className="map-dot faint" cx="148" cy="92" r="3" />
              </svg>
              <span className="map-pin">
                <MapPinned aria-hidden="true" size={20} strokeWidth={1.7} />
              </span>
            </div>
            <div className="map-info">
              <p className="card-label">Mapa</p>
              <h3>{pl("La Palette Garden")}</h3>
              <p>
                {pl("Gustawa Morcinka 40, 31-762 Kraków")}
              </p>
              <ul className="map-tags" aria-label="Informacje o miejscu">
                <li>Ceremonia</li>
                <li>Przyjęcie</li>
                <li>Kraków</li>
              </ul>
              <div className="venue-links">
                <a
                  href="https://www.google.com/maps/search/?api=1&query=Gustawa+Morcinka+40%2C+31-762+Krak%C3%B3w"
                  target="_blank"
                  rel="noreferrer"
                >
                  <MapPinned aria-hidden="true" size={16} />
                  Nawigacja
                </a>
                <a
                  href="https://www.lapalettegarden.pl/"
                  target="_blank"
                  rel="noreferrer"
                >
                  <ExternalLink aria-hidden="true" size={16} />
                  Strona miejsca
                </a>
              </div>
            </div>
          </section>
        </section>

        <section className="section timeline-section" id="plan">
          <SectionHeading eyebrow="Plan dnia" title="Ramowy przebieg uroczystości" />
          <ol className="timeline">
            {timeline.map((item, index) => {
              const Icon = item.icon;
              return (
                <li
                  className="timeline-item"
                  key={item.title}
                  data-reveal
                  style={{ "--reveal-delay": `${index * 60}ms` } as CSSProperties}
                >
                  <span className="timeline-marker" aria-hidden="true">
                    <Icon aria-hidden="true" size={17} strokeWidth={1.7} />
                  </span>
                  <div className="timeline-body">
                    <p className="timeline-time">{item.time}</p>
                    <h3>{pl(item.title)}</h3>
                    <p>{pl(item.text)}</p>
                  </div>
                </li>
              );
            })}
          </ol>
        </section>

        <section className="rsvp-section" id="potwierdzenie">
          <div className="rsvp-inner">
            <div className="rsvp-copy" data-reveal>
              <CalendarDays aria-hidden="true" size={32} strokeWidth={1.4} />
              <p className="eyebrow">Potwierdzenie obecności</p>
              <h2>{pl("Czy będziecie z nami?")}</h2>
              <p className="rsvp-lede">
                {pl(
                  "Wpiszcie wszystkie zgłaszane osoby i odpowiedzcie na kilka pytań. Dzięki temu zaplanujemy miejsca, menu, noclegi i transport.",
                )}
              </p>
              <dl className="event-ticket">
                <div>
                  <dt>Data</dt>
                  <dd>10 lipca 2027</dd>
                </div>
                <div>
                  <dt>Miejsce</dt>
                  <dd>La Palette Garden, Kraków</dd>
                </div>
                <div>
                  <dt>Godzina</dt>
                  <dd>Podamy wkrótce</dd>
                </div>
              </dl>
              <div className="transport-route" aria-label="Planowany transport">
                <span className="route-place">Wolbrom</span>
                <span className="route-line" aria-hidden="true">
                  <BusFront size={18} strokeWidth={1.7} />
                </span>
                <span className="route-place">Kraków</span>
              </div>
            </div>

            <form className="rsvp-form" onSubmit={handleRsvpSubmit} data-reveal>
              <FormField label="Imię / imiona i nazwiska">
                <input
                  required
                  name="names"
                  value={rsvp.names}
                  onChange={(event) => updateRsvp("names", event.target.value)}
                  placeholder="Np. Anna Kowalska i Jan Nowak"
                  autoComplete="name"
                  maxLength={240}
                />
              </FormField>

              <FormField label="Telefon kontaktowy (opcjonalnie)">
                <input
                  type="tel"
                  name="contactPhone"
                  value={rsvp.contactPhone}
                  onChange={(event) => updateRsvp("contactPhone", event.target.value)}
                  placeholder="Np. +48 600 000 000"
                  autoComplete="tel"
                  inputMode="tel"
                  maxLength={40}
                />
              </FormField>

              <ChoiceGroup
                legend="Czy będziecie z nami?"
                name="attendance"
                value={rsvp.attendance}
                options={[
                  { value: "yes", label: "Tak, będziemy" },
                  { value: "no", label: "Niestety nie możemy" },
                ]}
                onChange={(value) =>
                  updateRsvp("attendance", value as RsvpState["attendance"])
                }
              />

              {rsvp.attendance === "yes" ? (
                <div className="rsvp-logistics">
                  <FormField label="Alergie pokarmowe">
                    <textarea
                      required
                      name="foodAllergies"
                      value={rsvp.foodAllergies}
                      onChange={(event) =>
                        updateRsvp("foodAllergies", event.target.value)
                      }
                      placeholder="Wpisz alergie lub „brak”"
                      rows={3}
                      maxLength={1_000}
                    />
                  </FormField>

                  <ChoiceGroup
                    legend="Czy potrzebujecie noclegu?"
                    name="hotel"
                    value={rsvp.hotel}
                    options={[
                      { value: "yes", label: "Tak" },
                      { value: "no", label: "Nie" },
                    ]}
                    onChange={(value) =>
                      updateRsvp("hotel", value as RsvpState["hotel"])
                    }
                  />

                  <ChoiceGroup
                    legend="Autobus z Wolbromia do Krakowa"
                    name="outboundBus"
                    value={rsvp.outboundBus}
                    options={[
                      { value: "yes", label: "Potrzebuję miejsca" },
                      { value: "no", label: "Nie potrzebuję" },
                    ]}
                    onChange={(value) =>
                      updateRsvp("outboundBus", value as RsvpState["outboundBus"])
                    }
                  />

                  <ChoiceGroup
                    legend="Powrót do Wolbromia"
                    name="returnBus"
                    value={rsvp.returnBus}
                    options={returnTransportOptions}
                    wideLast
                    onChange={(value) =>
                      updateRsvp("returnBus", value as RsvpState["returnBus"])
                    }
                  />
                </div>
              ) : rsvp.attendance === "no" ? (
                <p className="decline-note">
                  {pl("Będzie nam Was brakowało. Wyślijcie odpowiedź, żebyśmy mogli ją zapisać.")}
                </p>
              ) : null}

              <label className="form-honeypot" aria-hidden="true">
                Strona internetowa
                <input
                  tabIndex={-1}
                  value={rsvp.website}
                  onChange={(event) => updateRsvp("website", event.target.value)}
                  autoComplete="off"
                />
              </label>
              <p className="rsvp-privacy">
                Odpowiedzi wykorzystamy wyłącznie do organizacji uroczystości.
              </p>
              <button
                className="button ink form-button"
                type="submit"
                disabled={rsvpSubmitState === "submitting"}
              >
                <Send aria-hidden="true" size={16} />
                <span>
                  {rsvpSubmitState === "submitting"
                    ? "Wysyłamy..."
                    : "Wyślij odpowiedź"}
                </span>
              </button>
              {rsvpSubmitMessage ? (
                <p
                  className={`form-status ${rsvpSubmitState}`}
                  role="status"
                  aria-live="polite"
                >
                  {pl(rsvpSubmitMessage)}
                </p>
              ) : null}
            </form>
          </div>
        </section>

        <section className="section gallery-section" id="galeria">
          <SectionHeading
            eyebrow="Galeria"
            title="Zdjęcia i wspomnienia"
            lede="Zostawiamy gotowe miejsce na zdjęcia z przygotowań, ceremonii i wesela. Pierwsze kadry dodamy, gdy będą gotowe."
          />
          <div className="gallery-grid">
            <figure className="gallery-frame" data-reveal>
              <img
                src="/images/wedding-hero.png"
                alt="Dekoracja stołu weselnego z kwiatami, świecami i obrączkami"
                loading="lazy"
              />
              <figcaption>
                {pl("Nasza strona startuje od spokojnego, ogrodowego klimatu.")}
              </figcaption>
            </figure>
            {galleryItems.map((item, index) => (
              <article
                className={`gallery-card gallery-card-${index + 1}`}
                key={item.title}
                data-reveal
                style={{ "--reveal-delay": `${index * 80}ms` } as CSSProperties}
              >
                <Sprig className="gallery-card-sprig" />
                <span className="gallery-card-title">{item.title}</span>
                <p>{pl(item.text)}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="section checklist-section">
          <div className="checklist-card" data-reveal>
            <Sprig />
            <p className="eyebrow">Przed weselem</p>
            <h2>{pl("Mała lista dla gości")}</h2>
            <ul className="checklist">
              {checklistItems.map((item) => (
                <li className="check-item" key={item}>
                  <span className="check-box" aria-hidden="true">
                    <Check aria-hidden="true" size={13} strokeWidth={2.6} />
                  </span>
                  <span>{pl(item)}</span>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="section faq-section" id="faq">
          <SectionHeading eyebrow="Pytania i odpowiedzi" title="Najczęstsze pytania" />
          <div className="faq-list" data-reveal>
            {faqItems.map((item) => (
              <details key={item.question}>
                <summary>{pl(item.question)}</summary>
                <p>{pl(item.answer)}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="section question-section" id="pytania">
          <div className="question-inner">
            <div className="question-copy" data-reveal>
              <Sprig />
              <p className="eyebrow">Pytania</p>
              <h2>Napisz do nas</h2>
              <p>
                {pl(
                  "Wyślij pytanie przez formularz. Wiadomość trafi bezpośrednio do nas, a odpowiedź odeślemy na podany adres.",
                )}
              </p>
              <p className="contact-note">
                {pl(
                  "To jedyne miejsce kontaktu na stronie, żeby wszystkie wiadomości były w jednym wątku.",
                )}
              </p>
            </div>

            <form className="question-form" onSubmit={handleQuestionSubmit} data-reveal>
              <FormField label="Imię">
                <input
                  required
                  value={question.name}
                  onChange={(event) => updateQuestion("name", event.target.value)}
                  placeholder="Jak się nazywasz?"
                  autoComplete="name"
                />
              </FormField>
              <FormField label="E-mail zwrotny">
                <input
                  required
                  type="email"
                  value={question.email}
                  onChange={(event) => updateQuestion("email", event.target.value)}
                  placeholder="twoj@email.pl"
                  autoComplete="email"
                />
              </FormField>
              <FormField label="Pytanie">
                <textarea
                  required
                  value={question.message}
                  onChange={(event) => updateQuestion("message", event.target.value)}
                  placeholder="O co chcesz zapytać?"
                  rows={5}
                />
              </FormField>
              <label className="form-honeypot" aria-hidden="true">
                Strona internetowa
                <input
                  tabIndex={-1}
                  value={question.website}
                  onChange={(event) => updateQuestion("website", event.target.value)}
                  autoComplete="off"
                />
              </label>
              <button
                className="button ink form-button"
                type="submit"
                disabled={submitState === "submitting"}
              >
                <Send aria-hidden="true" size={16} />
                <span>
                  {submitState === "submitting" ? "Wysyłamy..." : "Wyślij pytanie"}
                </span>
              </button>
              {submitMessage ? (
                <p
                  className={`form-status ${submitState}`}
                  role="status"
                  aria-live="polite"
                >
                  {pl(submitMessage)}
                </p>
              ) : null}
            </form>
          </div>
        </section>

      </main>

      <footer className="footer">
        <Seal className="footer-seal" />
        <p className="footer-names">
          Ewelina <em>i</em> Mateusz
        </p>
        <p className="footer-note">Do zobaczenia!</p>
        <a className="footer-link" href="#pytania">
          Formularz kontaktowy
        </a>
        <a className="footer-link" href="#potwierdzenie">
          Potwierdź obecność
        </a>
        <span className="footer-domain">ewelinaimateusz.pl</span>
      </footer>
    </div>
  );
}
